import { Form, Head } from "@inertiajs/react"
import { Suspense, lazy, useState } from "react"
import { useTranslation } from "react-i18next"

import BrandSettingsLayout from "@/components/brand/settings/brand-settings-layout"
import { CharterValuesEditor } from "@/components/brand/settings/charter-values-editor"
import type { CharterValueRow } from "@/components/brand/settings/charter-values-editor"
import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { OnboardingCharterBody } from "@/components/onboarding/onboarding-charter-body"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import AppLayout from "@/layouts/app-layout"
import { brandSettingsCharterPath } from "@/routes"

/**
 * OKS-40 — Brand Settings → Crew Charter page.
 *
 * Two visual modes (Edit / Preview), one toggle button. Preview
 * uses the same `<OnboardingCharterBody>` renderer creators see
 * at OKS-22 signup — WYSIWYG honesty (UX Spec §2.3).
 *
 * `<CharterEditor>` is dynamic-imported to keep TipTap (~55 kB gz)
 * out of the shared bundle (TRD §2 bundle-size note).
 */

const CharterEditor = lazy(() => import("@/components/editor/charter-editor"))

export interface BrandSettingsCharterProps {
  brand: {
    id: number
    crew_charter: string
  }
  max_length: number
  can_edit: boolean
  // OKS-383 — "Crew values" editor section data. Always three rows
  // (authored values prefilled, or blanks when unauthored).
  charter_values: CharterValueRow[]
  charter_value_max: { title: number; body: number }
  charter_value_icons: string[]
}

export default function BrandSettingsCharter({
  brand,
  max_length,
  can_edit,
  charter_values,
  charter_value_max,
  charter_value_icons,
}: BrandSettingsCharterProps) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<"edit" | "preview">("edit")

  // Local copy so Preview reflects in-flight edits without a save.
  const [draftHtml, setDraftHtml] = useState(brand.crew_charter)

  const isEmpty = brand.crew_charter.trim() === ""

  return (
    <AppLayout>
      <Head title={t("settings.brand.charter.title")} />
      <BrandSettingsLayout>
        <div data-testid="brand-settings-charter" className="space-y-6">
          <HeadingSmall
            title={t("settings.brand.charter.title")}
            description={t("settings.brand.charter.description")}
          />

          {isEmpty ? (
            <Alert
              data-testid="brand-settings-charter-default-banner"
              role="status"
            >
              <AlertDescription>
                {t("settings.brand.charter.default_banner")}
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() =>
                setMode((m) => (m === "edit" ? "preview" : "edit"))
              }
              data-testid="brand-settings-charter-toggle-mode"
            >
              {mode === "edit"
                ? t("settings.brand.charter.preview")
                : t("settings.brand.charter.edit")}
            </Button>
          </div>

          {mode === "preview" ? (
            <OnboardingCharterBody html={draftHtml} />
          ) : null}

          <Form
            method="patch"
            action={brandSettingsCharterPath()}
            options={{ preserveScroll: true }}
            className={mode === "edit" ? "space-y-4" : "hidden"}
            data-testid="brand-settings-charter-form"
            onChange={(event) => {
              // Mirror the hidden input into local state so Preview
              // reflects unsaved changes when the user toggles modes.
              const target = event.target as unknown as HTMLInputElement
              if (target.name === "brand[crew_charter]") {
                setDraftHtml(target.value)
              }
            }}
          >
            {({ errors, processing }) => (
              <>
                <Suspense
                  fallback={
                    <Skeleton
                      data-testid="brand-settings-charter-loading"
                      className="h-64 w-full"
                    />
                  }
                >
                  <CharterEditor
                    defaultValue={brand.crew_charter}
                    maxLength={max_length}
                    readOnly={!can_edit}
                  />
                </Suspense>

                <InputError
                  messages={errors.crew_charter}
                  data-testid="brand-settings-charter-error"
                />

                {can_edit ? (
                  <Button
                    type="submit"
                    disabled={processing}
                    data-testid="brand-settings-charter-save"
                  >
                    {processing
                      ? t("settings.brand.charter.saving")
                      : t("settings.brand.charter.save")}
                  </Button>
                ) : (
                  <p
                    role="status"
                    data-testid="brand-settings-charter-readonly"
                    className="text-muted-foreground text-sm"
                  >
                    {t("settings.brand.charter.read_only_hint")}
                  </p>
                )}
              </>
            )}
          </Form>

          {/* OKS-383 — Crew values editor, below the rich-text charter. */}
          <hr className="border-border/60" />
          <CharterValuesEditor
            values={charter_values}
            icons={charter_value_icons}
            maxLengths={charter_value_max}
            canEdit={can_edit}
          />
        </div>
      </BrandSettingsLayout>
    </AppLayout>
  )
}
